import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  DollarSign, PieChart, LineChart, Landmark, TrendingUp, Calendar,
  ShoppingCart, School, ArrowRight, User, Download,
} from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { fetchInvestorHome } from "@/repositories/investorRepository";
import type { InvestorHomeData } from "@/domain/investor";
import { formatMoney, formatPercent, formatDate } from "@/utils/format";

export function InvestorHomePage() {
  const [data, setData] = useState<InvestorHomeData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchInvestorHome()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  return (
    <AppShell
      portal="Investor"
      userName={data?.investorDisplayName}
      userRole="Investor"
      unreadCount={data?.unreadNotificationCount ?? 0}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28 }}>Home</h1>
          <p style={{ margin: "6px 0 0", fontSize: 18, fontWeight: 600 }}>
            Welcome back, {data?.investorDisplayName ?? "…"}!
          </p>
        </div>

        {data && (
          <div className="dashboard-card" style={{ display: "flex", alignItems: "center", gap: 18, padding: "16px 22px" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.15 }}>
              <img src="/brand/logo-default.png" alt="AlifBee" width={35} height={35} style={{ objectFit: "contain", marginBottom: 4 }} />
              <span style={{ fontWeight: 800, fontSize: 17 }}>AlifBee</span>
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Investor Portal</span>
            </div>
            <div>
              <div style={{ fontWeight: 700 }}>{data.companyName}</div>
              <a className="link-arrow" href="#" onClick={(e) => e.preventDefault()}>
                View company profile <ArrowRight size={13} />
              </a>
            </div>
          </div>
        )}
      </div>

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {!data && !error && <div className="loading-state">Loading live data from Google Sheets…</div>}

      {data && (
        <>
          <div className="dashboard-grid equal-height">
            <div className="dashboard-card">
              <h3>My Investment Summary</h3>
              <StatRow icon={DollarSign} color="strong-blue" label="Amount Invested" value={formatMoney(data.investment.amountPaid)} />
              <StatRow icon={PieChart} color="strong-blue" label="Shares Owned" value={`${data.investment.sharesOwned.toLocaleString()} ${data.investment.shareClassCode}`} />
              <StatRow icon={LineChart} color="strong-blue" label="Estimated Value" value={formatMoney(data.investment.estimatedValue, true)} valueClass="value-green" />
              <Link to="/investor/my-investment" className="stat-row ownership-link-row" style={{ textDecoration: "none", color: "inherit" }}>
                <div className="stat-row-left">
                  <div className="icon-badge strong-blue"><User size={16} /></div>
                  <span style={{ fontSize: 14 }}>Ownership (Diluted)</span>
                </div>
                <span className="value-strong">{formatPercent(data.investment.ownershipPct)}</span>
              </Link>
              <div className="card-footer">
                <a className="link-arrow" href="/investor/my-investment">View details <ArrowRight size={13} /></a>
                <span>As of {formatDate(data.investment.asOfDate)}</span>
              </div>
            </div>

            <div className="dashboard-card">
              <h3>Latest Company Valuation</h3>
              <StatRow icon={Landmark} color="strong-blue" label="Company Valuation" value={formatMoney(data.valuation.companyValuation)} />
              <StatRow icon={TrendingUp} color="strong-blue" label="Sales Multiple" value={data.valuation.salesMultiple ? `${data.valuation.salesMultiple}×` : "—"} />
              <StatRow icon={Calendar} color="strong-blue" label="Valuation Date" value={formatDate(data.valuation.valuationDate)} />
              <div className="card-footer" style={{ justifyContent: "flex-start" }}>
                <a className="link-arrow" href="/investor/valuation">View valuation history <ArrowRight size={13} /></a>
              </div>
            </div>

            <div className="dashboard-card">
              <h3>Performance (YTD)</h3>
              <StatRow icon={DollarSign} color="green" label="Annual Sales" value={formatMoney(data.performance.annualSales)} />
              <StatRow icon={ShoppingCart} color="purple" label="Online Sales" value={formatMoney(data.performance.onlineSales)} />
              <StatRow icon={School} color="blue" label="Schools Sales" value={formatMoney(data.performance.schoolsSales)} />
              <StatRow icon={TrendingUp} color="orange" label={`${data.performance.projectedPeriod ?? ""} Projected Sales`} value={formatMoney(data.performance.projectedSales)} />
              <div className="card-footer" style={{ justifyContent: "flex-start" }}>
                <a className="link-arrow" href="/investor/company-performance">View full performance <ArrowRight size={13} /></a>
              </div>
            </div>
          </div>

          <div className="dashboard-grid cols-2">
            <div className="dashboard-card">
              <h3>Latest Update</h3>
              {data.latestUpdate ? (
                <>
                  <div className="update-thumbnail">
                    <div className="thumb-title">{data.latestUpdate.title}</div>
                    <TrendingUp className="thumb-chart" size={56} strokeWidth={1.5} />
                  </div>
                  <div style={{ fontWeight: 600, color: "var(--accent)" }}>{data.latestUpdate.title}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0 10px" }}>
                    {formatDate(data.latestUpdate.publishDate)}
                  </div>
                  <p style={{ fontSize: 14, margin: 0 }}>{data.latestUpdate.description}</p>
                  <div className="card-footer" style={{ justifyContent: "flex-start" }}>
                    <a className="link-arrow" href="/investor/notifications">Read update <ArrowRight size={13} /></a>
                  </div>
                </>
              ) : (
                <div className="empty-state">No published updates yet.</div>
              )}
            </div>

            <div className="dashboard-card">
              <h3>Latest Documents</h3>
              {data.latestDocuments.length > 0 ? (
                <>
                  {data.latestDocuments.map((doc) => (
                    <div key={doc.fileName} className="stat-row">
                      <div className="stat-row-left">
                        <div className="doc-type-badge">{doc.fileType}</div>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 14 }}>{doc.fileName}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{formatDate(doc.dateAdded)}</span>
                        {doc.canDownload ? (
                          <Download size={16} color="var(--accent)" style={{ cursor: "pointer" }} />
                        ) : (
                          <Download size={16} color="var(--border)" style={{ cursor: "not-allowed" }} />
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="card-footer" style={{ justifyContent: "flex-start" }}>
                    <a className="link-arrow" href="/investor/documents/my-investment">View all documents <ArrowRight size={13} /></a>
                  </div>
                </>
              ) : (
                <div className="empty-state">No documents yet.</div>
              )}
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}

function StatRow({ icon: Icon, color, label, value, valueClass }: {
  icon: typeof DollarSign;
  color: "blue" | "green" | "purple" | "orange" | "gray" | "strong-blue";
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="stat-row">
      <div className="stat-row-left">
        <div className={`icon-badge ${color}`}><Icon size={16} /></div>
        <span style={{ fontSize: 14 }}>{label}</span>
      </div>
      <span className={`value-strong ${valueClass ?? ""}`}>{value}</span>
    </div>
  );
}
